import { useTranslation } from '@hbs-us/ui';
import { useState } from 'react';

import type { DispatchAssignmentDto } from './contractsMirror.js';

/**
 * The per-assignment action row shown on every scheduled card (board column + day agenda). Three
 * keyboard-operable affordances, each with a specific t()-keyed label (never an icon alone):
 *
 *   • Copy status link — builds the field-crew status-page URL from the CURRENT tenant origin
 *     (`window.location.origin`, i.e. the tenant host the dashboard is served on) + `/dispatch/job/`
 *     + the assignment's `publicToken`, and copies it to the clipboard. Only shown when the server
 *     surfaced a token. The token IS the capability (T-22-23-token) — the link is safe to share with
 *     the assigned crew member.
 *   • Edit  — opens the full-field edit dialog (parent-owned).
 *   • Remove — opens the destructive unassign confirm (parent-owned).
 *
 * Token-only styling — no hex (BRAND-06).
 */

function statusUrlFor(token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/dispatch/job/${token}`;
}

export function AssignmentCardActions({
  assignment,
  onEdit,
  onUnassign,
}: {
  assignment: DispatchAssignmentDto;
  onEdit: (assignment: DispatchAssignmentDto) => void;
  onUnassign: (assignment: DispatchAssignmentDto) => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!assignment.publicToken) return;
    try {
      await navigator.clipboard?.writeText(statusUrlFor(assignment.publicToken));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (older browser / insecure context) — no-op; the Edit dialog still
      // shows the link text so the dispatcher can copy it manually.
      setCopied(false);
    }
  };

  const BTN =
    'inline-flex h-7 items-center rounded-sm px-2 font-medium text-[0.7rem] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {assignment.publicToken ? (
        <button
          type="button"
          onClick={() => void handleCopy()}
          className={`${BTN} bg-secondary text-secondary-foreground hover:bg-secondary/80`}
        >
          {copied ? t('dispatch:card.linkCopied') : t('dispatch:card.copyLink')}
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onEdit(assignment)}
        className={`${BTN} bg-secondary text-secondary-foreground hover:bg-secondary/80`}
      >
        {t('dispatch:card.edit')}
      </button>
      <button
        type="button"
        onClick={() => onUnassign(assignment)}
        className={`${BTN} bg-destructive/10 text-destructive hover:bg-destructive/20`}
      >
        {t('dispatch:card.remove')}
      </button>
    </div>
  );
}
